import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import SecondaryButton from "@/Components/UI/Forms/SecondaryButton";
import TextAreaInput from "@/Components/UI/Forms/TextAreaInput";
import { PageProps } from "@/types/page";
import { Appointment } from "@/types/scheduling/appointment";
import { usePage } from "@inertiajs/react";
import { addMinutes } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useState } from "react";
import { BiCalendar, BiSync } from "react-icons/bi";
import { TbCalendarOff } from "react-icons/tb";
import {
    blockerKey,
    DayBlocker,
    SESSION_DURATION_MINUTES,
} from "./dateTimePickerUtils";

type ConfirmationProps = {
    selectedTime: Date;
    isRecurring: boolean;
    recurringIntervalCount: number | null;
    reschedulingAppointment?: Appointment;
    conflictingBlockers: DayBlocker[];
    onConfirm: (slot: {
        start: string;
        end: string;
        isRecurring: boolean;
        note?: string;
    }) => void;
    onBack: () => void;
};

const MINIMUM_RESCHEDULE_NOTE_LENGTH = 3;

export default function Confirmation({
    selectedTime,
    isRecurring,
    recurringIntervalCount,
    reschedulingAppointment,
    conflictingBlockers,
    onConfirm,
    onBack,
}: ConfirmationProps) {
    const { user } = usePage<PageProps>().props;
    const timezone = user?.timezone ?? "UTC";

    const [note, setNote] = useState("");

    const isRescheduling = !!reschedulingAppointment;
    const requiresRescheduleNote = isRescheduling && user?.type === "coach";
    const rescheduleNoteTooShort =
        requiresRescheduleNote &&
        note.trim().length < MINIMUM_RESCHEDULE_NOTE_LENGTH;

    const weekPhrasing =
        recurringIntervalCount === 1
            ? "every week"
            : `every ${recurringIntervalCount} weeks`;

    return (
        <div className="flex flex-col gap-2 md:min-w-100">
            <div>
                <p className="text-lg font-bold text-brand-black mb-2">
                    {isRescheduling
                        ? "Confirm rescheduled session"
                        : isRecurring
                          ? "Confirm recurring session"
                          : "Confirm session"}
                </p>
            </div>

            {isRescheduling && (
                <div className="flex gap-2 line-through">
                    <BiCalendar className="shrink-0 mt-[4px]" />
                    {formatInTimeZone(
                        reschedulingAppointment?.start_at,
                        timezone,
                        "EEEE MMMM d, yyyy 'at' h:mm a zzz",
                    )}
                </div>
            )}
            <div data-testid="confirmation-session-time" className="flex gap-2">
                <BiCalendar className="shrink-0 mt-[4px]" />
                {formatInTimeZone(
                    selectedTime,
                    timezone,
                    "EEEE MMMM d, yyyy 'at' h:mm a zzz",
                )}
            </div>
            {isRecurring && recurringIntervalCount !== null && (
                <div
                    data-testid="confirmation-session-recurring-time"
                    className="flex gap-2"
                >
                    <BiSync
                        size={20}
                        className="shrink-0 -mr-[2px] -ml-[2px] mt-[3px]"
                    />
                    Repeating {weekPhrasing}
                </div>
            )}

            {!!conflictingBlockers.length && (
                <div
                    data-testid="confirmation-conflicts"
                    className="flex flex-col gap-2 rounded-lg border border-brand-dangerOrange bg-brand-dangerOrange/10 p-3 mt-2"
                >
                    <div className="flex items-center gap-2 font-medium text-brand-black">
                        <TbCalendarOff
                            size={20}
                            className="shrink-0 text-brand-dangerOrange"
                        />
                        This time conflicts with your schedule
                    </div>
                    {conflictingBlockers.map((blocker) => (
                        <div
                            key={blockerKey(blocker)}
                            data-testid="confirmation-conflict"
                            className="text-sm text-brand-gray"
                        >
                            {blocker.name} (
                            {formatInTimeZone(
                                blocker.start,
                                timezone,
                                "h:mm a",
                            )}
                            {" - "}
                            {formatInTimeZone(blocker.end, timezone, "h:mm a")})
                        </div>
                    ))}
                </div>
            )}

            {requiresRescheduleNote && (
                <TextAreaInput
                    data-testid="reschedule-note-input"
                    containerClassName="mt-2"
                    className="w-full"
                    value={note}
                    placeholder="Please include a note for your client about why you are rescheduling."
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={255}
                />
            )}

            <div className="flex flex-col gap-3 sm:flex-row mt-4">
                <PrimaryButton
                    data-testid="confirm-session-button"
                    className="grow justify-center py-3 sm:order-2 sm:w-0"
                    disabled={rescheduleNoteTooShort}
                    onClick={() =>
                        onConfirm({
                            start: selectedTime.toISOString(),
                            end: addMinutes(
                                selectedTime,
                                SESSION_DURATION_MINUTES,
                            ).toISOString(),
                            isRecurring,
                            note: requiresRescheduleNote ? note : undefined,
                        })
                    }
                >
                    Confirm
                </PrimaryButton>
                <SecondaryButton
                    data-testid="back-to-times-button"
                    className="grow justify-center py-3 sm:w-0"
                    onClick={onBack}
                >
                    Back
                </SecondaryButton>
            </div>
        </div>
    );
}
